<template>
  <div class="pa-2">
    <v-row v-if="isLoading" justify="center" class="mt-10">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </v-row>

    <div v-else>
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4" color="#2A2A2A" dark elevation="4">
            <div class="caption grey--text text--lighten-1">Gesamt Check-ins</div>
            <div class="text-h4 font-weight-bold primary--text">{{ kpis.totalCheckins }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4" color="#2A2A2A" dark elevation="4">
            <div class="caption grey--text text--lighten-1">Durchschnitt pro Tag</div>
            <div class="text-h4 font-weight-bold white--text">{{ kpis.averagePerDay }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4" color="#2A2A2A" dark elevation="4">
            <div class="caption grey--text text--lighten-1">Beliebteste Uhrzeit</div>
            <div class="text-h4 font-weight-bold white--text">{{ kpis.busiestHour }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="rounded-xl pa-4" color="#2A2A2A" dark elevation="4">
            <div class="caption grey--text text--lighten-1">Stärkster Wochentag</div>
            <div class="text-h4 font-weight-bold white--text">{{ kpis.busiestDay }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="rounded-xl pa-3" color="#212121" dark>
            <v-card-title class="font-weight-bold text-subtitle-1 pb-0">Check-in Verlauf</v-card-title>
            <apexchart height="300" :options="timelineOptions" :series="timelineSeries"></apexchart>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12" md="6">
          <v-card class="rounded-xl pa-3" color="#212121" dark>
            <v-card-title class="font-weight-bold text-subtitle-1 pb-0">Auslastung nach Uhrzeit</v-card-title>
            <apexchart height="300" :options="hourlyOptions" :series="hourlySeries"></apexchart>
          </v-card>
        </v-col>

        <v-col cols="12" md="6">
          <v-card class="rounded-xl pa-3" color="#212121" dark>
            <v-card-title class="font-weight-bold text-subtitle-1 pb-0">Besuche nach Wochentag</v-card-title>
            <apexchart height="300" :options="weekdayOptions" :series="weekdaySeries"></apexchart>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mt-2" v-if="studios.length > 1">
        <v-col cols="12">
          <v-card class="rounded-xl pa-3" color="#212121" dark>
            <v-card-title class="font-weight-bold text-subtitle-1 pb-0">Studio Verteilung (Top List)</v-card-title>
            <apexchart height="350" :options="studioRankingOptions" :series="studioRankingSeries"></apexchart>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import api from "../../../api"

export default {
  name: "CheckinDashboardV2",
  props: {
    datePickerRange: {
      type: Array,
      default: () => [],
    },
    studios: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: true,
      kpis: {
        totalCheckins: 0,
        averagePerDay: 0,
        busiestHour: "-",
        busiestDay: "-",
      },
      rawCharts: {
        timeline: [],
        hourly: [],
        weekdays: {},
        studios: [],
      },
    }
  },
  async created() {
    await this.fetchData()
  },
  watch: {
    async datePickerRange() {
      await this.fetchData()
    },
    async studios() {
      await this.fetchData()
    },
  },
  computed: {
    // --- TIMELINE (Area Chart) ---
    timelineSeries() {
      return [{ name: "Check-ins", data: this.rawCharts.timeline }]
    },
    timelineOptions() {
      return {
        chart: { type: "area", foreColor: "#ccc", toolbar: { show: false } },
        colors: [this.$vuetify.theme.themes.light.primary || "#FF5722"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } },
        xaxis: { type: "datetime" },
        tooltip: { theme: "dark", x: { format: "dd MMM yyyy" } },
      }
    },

    // --- HOURLY PEAKS (Bar Chart) ---
    hourlySeries() {
      return [{ name: "Check-ins", data: this.rawCharts.hourly.map((h) => h.count) }]
    },
    hourlyOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc", toolbar: { show: false } },
        colors: ["#4CAF50"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.rawCharts.hourly.map((h) => `${h.hour}:00`),
          labels: { style: { fontSize: "10px" } },
        },
        tooltip: { theme: "dark" },
      }
    },

    // --- WEEKDAYS (Radar or Bar Chart) ---
    weekdaySeries() {
      const order = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"]
      const data = order.map((day) => this.rawCharts.weekdays[day] || 0)
      return [{ name: "Check-ins", data: data }]
    },
    weekdayOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc", toolbar: { show: false } },
        colors: ["#2196F3"],
        plotOptions: { bar: { horizontal: false, borderRadius: 4, distributed: true } },
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: { categories: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] },
        tooltip: { theme: "dark" },
      }
    },

    // --- STUDIO RANKING (Horizontal Bar Chart) ---
    studioRankingSeries() {
      return [{ name: "Check-ins", data: this.rawCharts.studios.map((s) => s.count) }]
    },
    studioRankingOptions() {
      return {
        chart: { type: "bar", foreColor: "#ccc", toolbar: { show: false } },
        colors: [this.$vuetify.theme.themes.light.primary || "#FF5722"],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, dataLabels: { position: "top" } } },
        dataLabels: {
          enabled: true,
          offsetX: 20,
          style: { fontSize: "12px", colors: ["#fff"] },
        },
        xaxis: { categories: this.rawCharts.studios.map((s) => s.name) },
        tooltip: { theme: "dark" },
      }
    },
  },
  methods: {
    async fetchData() {
      if (!this.datePickerRange || this.datePickerRange.length < 2 || !this.studios.length) return

      this.isLoading = true
      try {
        const response = await api.fetchCheckinStatisticsV2(this.datePickerRange, this.studios)
        this.kpis = response.kpis
        this.rawCharts = response.charts
      } catch (error) {
        console.error("Dashboard Daten konnten nicht geladen werden", error)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
/* Nur ein bisschen Fein-Tuning falls nötig, die v-cards regeln fast alles */
</style>
